import { useEffect, useState } from "react";
import { Flag, Strikethrough, ListChecks } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const readStrikes = (k) => {
  if (!k) return {};
  try { return JSON.parse(localStorage.getItem(`pm-strikes-${k}`) || "{}"); } catch { return {}; }
};

export function useStrikes(storageKey) {
  const [state, setState] = useState(() => ({ key: storageKey, strikes: readStrikes(storageKey) }));
  // Re-load when the session/attempt changes so strikes never leak across attempts.
  const current = state.key === storageKey ? state : { key: storageKey, strikes: readStrikes(storageKey) };
  if (current !== state) setState(current);
  const strikes = current.strikes;
  useEffect(() => {
    if (!state.key) return;
    try { localStorage.setItem(`pm-strikes-${state.key}`, JSON.stringify(state.strikes)); } catch { /* quota */ }
  }, [state]);
  const setStrikes = (fn) => setState((st) => ({ ...st, strikes: fn(st.strikes) }));
  const toggle = (key, i) => setStrikes((s) => {
    const cur = new Set(s[key] || []);
    if (cur.has(i)) cur.delete(i); else cur.add(i);
    return { ...s, [key]: [...cur] };
  });
  return [strikes, toggle];
}

export function StrikeButton({ on, onClick, testid }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onClick(); }} title={on ? "Undo strike-out" : "Strike out this choice"}
      className="shrink-0 w-9 rounded-xl flex items-center justify-center transition-colors"
      style={{ background: on ? "var(--text)" : "var(--muted)", color: on ? "var(--paper)" : "var(--text-muted)" }}
      data-testid={testid}>
      <Strikethrough size={14} />
    </button>
  );
}

export const struckStyle = { textDecoration: "line-through", opacity: 0.45 };

const FILTERS = [["all", "Review all"], ["incomplete", "Review incomplete"], ["flagged", "Review flagged"]];

export function ReviewScreen({ open, onOpenChange, items, onJump, title = "Review screen" }) {
  const [filter, setFilter] = useState("all");
  const shown = items.filter((it) => filter === "all" || (filter === "incomplete" ? !it.answered : it.flagged));
  const incomplete = items.filter((it) => !it.answered).length;
  const flagged = items.filter((it) => it.flagged).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="exam-review-screen">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListChecks size={18} /> {title}</DialogTitle>
          <DialogDescription data-testid="exam-review-summary">
            {items.length} questions · {incomplete} incomplete · {flagged} flagged
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 flex-wrap">
          {FILTERS.map(([id, label]) => (
            <button key={id} onClick={() => setFilter(id)} className="pm-badge"
              style={{ background: filter === id ? "var(--primary)" : "var(--muted)", color: filter === id ? "white" : "var(--text-secondary)" }}
              data-testid={`exam-review-filter-${id}`}>{label}</button>
          ))}
        </div>
        <div className="max-h-[50vh] overflow-y-auto divide-y" style={{ borderColor: "var(--border)" }}>
          {shown.length === 0 && <p className="text-sm py-6 text-center" style={{ color: "var(--text-muted)" }} data-testid="exam-review-empty">Nothing here.</p>}
          {shown.map((it) => (
            <button key={it.index} onClick={() => { onJump(it.index); onOpenChange(false); }}
              className="w-full flex items-center gap-3 py-2.5 px-1 text-left text-sm hover:opacity-75" data-testid={`exam-review-item-${it.index + 1}`}>
              <span className="font-mono w-8">{it.index + 1}</span>
              <span className="flex-1 truncate" style={{ color: "var(--text-secondary)" }}>{it.label}</span>
              {it.flagged && <Flag size={13} style={{ color: "#e08a2a" }} />}
              <span className="text-xs w-20 text-right" style={{ color: it.answered ? "var(--mastery)" : "var(--weakness)" }}>{it.answered ? "Complete" : "Incomplete"}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
